import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { format, getDaysInMonth } from 'date-fns';
import { enGB, sv } from 'date-fns/locale';
import { DatepickerLocaleService } from '../locale/datepicker-locale.service';

@Component({
  selector: 'lib-date-select',
  templateUrl: './date-select.component.html',
  styleUrls: ['./date-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DateSelectComponent implements OnInit, AfterViewInit {
  private _year: number;
  @Input() set year(value: number) {
    this._year = value;
    this.recalculateCalendar();
  }
  get year() {
    return this._year;
  }

  private _month: number;
  @Input() set month(value: number) {
    this._month = value;
    this.recalculateCalendar();
  }
  get month() {
    return this._month;
  }

  @Input() initialDate: number;
  @Input() minDate: number;
  @Input() maxDate: number;

  @Input() set focusDate(value: number) {
    if (value) {
      (document.activeElement as HTMLElement)?.blur();
      this.zone.runOutsideAngular(() => {
        setTimeout(() => {
          const element = document.querySelector(
            `#day-${value}`,
          ) as HTMLElement;
          if (element) {
            element.focus();
          }
        });
      });
    }
  }

  @ViewChild('datepickerTableRef')
  datepickerTableRef: ElementRef;

  @Output() selectedDate = new EventEmitter<number>();

  days: { date: number; otherMonth: boolean }[][] = [];

  weekdays: string[];
  weekdaysFull: string[];

  constructor(
    private datepickerLocaleService: DatepickerLocaleService,
    private translocoService: TranslocoService,
    private zone: NgZone,
  ) {}

  ngOnInit() {
    this.weekdays = this.datepickerLocaleService.getWeekdays();
    this.weekdaysFull = this.datepickerLocaleService.getWeekdaysFull();
    if (this.initialDate === undefined) {
      this.initialDate = new Date().getDate();
      if (this.minDate) this.initialDate = this.minDate;
    }
  }

  ngAfterViewInit(): void {
    this.focusDate = this.initialDate;
  }

  selectDate(day: { date: number; otherMonth: boolean }) {
    if (day.date > this.maxDate || day.date < this.minDate) {
      return;
    }

    if (!day.otherMonth) {
      this.selectedDate.emit(day.date);
    }
  }

  isDisabled(day: { date: number; otherMonth: boolean }) {
    return day.otherMonth || day.date > this.maxDate || day.date < this.minDate;
  }

  getDateLocale(date: number) {
    return format(new Date(this.year, this.month, date), 'PPP', {
      locale: this.translocoService.getActiveLang() === 'sv' ? sv : enGB,
    });
  }

  private getStartOfMonth(year, month, startWeekWithSunday: boolean) {
    const startOfMonth = new Date(year, month).getDay();
    if (startWeekWithSunday) {
      return startOfMonth;
    }

    return startOfMonth === 0 ? 6 : startOfMonth - 1;
  }

  private getNumberOfDaysInMonth(year: number, month: number): number {
    return getDaysInMonth(new Date(year, month, 1));
  }

  private getDateArray(length: number, startDate: number, otherMonth: boolean) {
    return Array.from({ length }, (_v, i) => i + startDate).map((d) => {
      return { date: d, otherMonth };
    });
  }

  private recalculateCalendar() {
    if ((!this.month && this.month !== 0) || !this.year) {
      return;
    }

    const startOfMonth = this.getStartOfMonth(this.year, this.month, false);
    const numberOfDaysInPrevMonth =
      this.month === 0
        ? this.getNumberOfDaysInMonth(this.year - 1, 11)
        : this.getNumberOfDaysInMonth(this.year, this.month - 1);

    const daysInPrevMonth = this.getDateArray(
      startOfMonth,
      numberOfDaysInPrevMonth - startOfMonth + 1,
      true,
    );

    const daysInMonth = this.getDateArray(
      this.getNumberOfDaysInMonth(this.year, this.month),
      1,
      false,
    );

    const monthDays = [];
    let i = 0;
    let chunk = 7 - startOfMonth;
    while (i < daysInMonth.length) {
      monthDays.push(daysInMonth.slice(i, i + chunk));
      i += chunk;
      chunk = 7;
    }

    this.days[0] = [...daysInPrevMonth, ...monthDays[0]];
    this.days[1] = monthDays[1];
    this.days[2] = monthDays[2];
    this.days[3] = monthDays[3];
    switch (monthDays.length) {
      case 4: {
        this.days[3] = monthDays[3];
        this.days[4] = this.getDateArray(7, 1, true);
        this.days[5] = this.getDateArray(7, this.days[4][6].date + 1, true);

        break;
      }
      case 5: {
        this.days[4] = [
          ...monthDays[4],
          ...this.getDateArray(7 - monthDays[4].length, 1, true),
        ];
        this.days[5] = this.getDateArray(
          7,
          this.days[4][6].otherMonth ? this.days[4][6].date + 1 : 1,
          true,
        );

        break;
      }
      case 6: {
        this.days[4] = monthDays[4];
        this.days[5] = [
          ...monthDays[5],
          ...this.getDateArray(7 - monthDays[5].length, 1, true),
        ];

        break;
      }
    }
  }
}
